import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import embed from 'vega-embed';

/**
 * Generate a unique filename for a chart download.
 * Format: <slugified-title>_<YYYYMMDD-HHmmss>_<4-digit-random>.png
 *
 * Priority for the name:
 *   1. spec.title  (set by the server to the measure/chart name)
 *   2. y-axis title from encoding (measure column name)
 *   3. x-axis title from encoding (dimension column name)
 *   4. mark type (bar / line / arc …)
 * spec.description is intentionally skipped – it contains the raw user query.
 */
function generateFilename(spec) {
  // Derive a human-readable slug – never use spec.description (raw query text)
  const rawTitle =
    spec?.title ||
    spec?.encoding?.y?.axis?.title ||
    spec?.encoding?.y?.title ||
    spec?.encoding?.x?.axis?.title ||
    spec?.encoding?.x?.title ||
    (typeof spec?.mark === 'string' ? spec.mark : spec?.mark?.type) ||
    'chart';

  const slug = String(rawTitle)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  // Local datetime stamp: YYYYMMDD-HHmmss
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  // 4-digit random suffix for extra uniqueness when downloading multiple charts
  const rand = Math.floor(1000 + Math.random() * 9000);

  return `${slug}_${datePart}-${timePart}_${rand}.png`;
}

/**
 * Deep-merge the dark theme config into any incoming Vega-Lite spec so that
 * chart titles, axis labels, legend text, and headers are always readable on
 * the dark application background — even when the backend spec omits some or
 * all config keys.
 */
const DARK_THEME_CONFIG = {
  background: '#1a1a1a',
  title: {
    color: '#ffffff',
    subtitleColor: '#cccccc',
    anchor: 'middle',
    fontSize: 14,
    fontWeight: 'bold',
  },
  text: { color: '#ffffff' },
  axis: {
    domainColor: '#ffffff',
    gridColor: '#444444',
    tickColor: '#ffffff',
    labelColor: '#ffffff',
    titleColor: '#ffffff',
    grid: true,
    gridOpacity: 0.3,
  },
  axisX: {
    labelColor: '#ffffff',
    titleColor: '#ffffff',
    domainColor: '#ffffff',
    tickColor: '#ffffff',
  },
  axisY: {
    labelColor: '#ffffff',
    titleColor: '#ffffff',
    domainColor: '#ffffff',
    tickColor: '#ffffff',
  },
  legend: {
    labelColor: '#ffffff',
    titleColor: '#ffffff',
    strokeColor: '#444444',
  },
  header: {
    labelColor: '#ffffff',
    titleColor: '#ffffff',
  },
  view: { strokeWidth: 0 },
};

function applyDarkTheme(spec) {
  if (!spec) return spec;
  const existingConfig = spec.config || {};
  return {
    ...spec,
    config: {
      // Base dark theme defaults
      ...DARK_THEME_CONFIG,
      // Merge any server-provided config keys (non-colour ones are preserved)
      ...existingConfig,
      // Hard-enforce all colour-critical keys so text is NEVER invisible
      background: '#1a1a1a',
      title: {
        ...DARK_THEME_CONFIG.title,
        ...(existingConfig.title || {}),
        color: '#ffffff',
        subtitleColor: '#cccccc',
      },
      text: { color: '#ffffff' },
      axis: {
        ...DARK_THEME_CONFIG.axis,
        ...(existingConfig.axis || {}),
        labelColor: '#ffffff',
        titleColor: '#ffffff',
        domainColor: '#ffffff',
        tickColor: '#ffffff',
      },
      axisX: {
        ...DARK_THEME_CONFIG.axisX,
        ...(existingConfig.axisX || {}),
        labelColor: '#ffffff',
        titleColor: '#ffffff',
        domainColor: '#ffffff',
        tickColor: '#ffffff',
      },
      axisY: {
        ...DARK_THEME_CONFIG.axisY,
        ...(existingConfig.axisY || {}),
        labelColor: '#ffffff',
        titleColor: '#ffffff',
        domainColor: '#ffffff',
        tickColor: '#ffffff',
      },
      legend: {
        ...DARK_THEME_CONFIG.legend,
        ...(existingConfig.legend || {}),
        labelColor: '#ffffff',
        titleColor: '#ffffff',
      },
      header: {
        ...DARK_THEME_CONFIG.header,
        ...(existingConfig.header || {}),
        labelColor: '#ffffff',
        titleColor: '#ffffff',
      },
    },
  };
}

function VegaChart({ spec }) {
  const chartRef = useRef(null);
  const vegaViewRef = useRef(null);   // holds the vega-embed result.view
  const [isDownloading, setIsDownloading] = useState(false);

  // Apply dark theme defensively so all text is always visible.
  // useMemo keeps the object reference stable across renders when spec hasn't
  // changed, making it safe to use in useEffect / useCallback dep arrays.
  const themedSpec = useMemo(() => applyDarkTheme(spec), [spec]);

  useEffect(() => {
    if (!themedSpec || !chartRef.current) {
      return;
    }

    // Validate that the spec has valid encoding
    const hasValidEncoding = themedSpec.encoding &&
                            Object.keys(themedSpec.encoding).length > 0 &&
                            (themedSpec.encoding.x || themedSpec.encoding.y || themedSpec.encoding.theta);
    
    if (!hasValidEncoding) {
      console.warn('[VEGA CHART] ⚠️ Invalid or empty encoding - skipping chart render');
      console.warn('[VEGA CHART] Encoding received:', themedSpec.encoding);
      return;
    }

    if (themedSpec.data && themedSpec.data.values) {
    }
    
    // Embed Vega-Lite chart
    // Use SVG renderer so tooltips on right-side bars are not clipped by the
    // canvas element boundary — SVG tooltips are positioned relative to the
    // page/viewport and are never cut off at the chart container edge.
    embed(chartRef.current, themedSpec, {
      actions: false,
      renderer: 'svg'
    })
      .then((result) => {
        // Store the view so we can use it for PNG export
        vegaViewRef.current = result.view;
        
        // Check if the view actually rendered
        setTimeout(() => {
          const canvas = chartRef.current?.querySelector('canvas');
          const svg = chartRef.current?.querySelector('svg');
          console.log('[VEGA CHART] Render check:', {
            hasCanvas: !!canvas,
            hasSvg: !!svg,
            canvasDimensions: canvas ? { width: canvas.width, height: canvas.height } : null,
            containerChildren: chartRef.current?.children.length
          });
        }, 100);
      })
      .catch((error) => {
        console.error('[VEGA CHART] ❌ Error embedding chart:', error);
        console.error('[VEGA CHART] Error details:', {
          message: error.message,
          stack: error.stack
        });
      });

    // Cleanup: clear the stored view when spec changes or component unmounts
    return () => {
      vegaViewRef.current = null;
    };
  }, [themedSpec]);

  /**
   * Download the chart as a PNG image.
   *
   * Strategy:
   *   1. Prefer vega view.toImageURL('png') — works with both canvas and SVG renderers.
   *   2. Fallback: serialise the rendered <svg> element to a canvas and export from there.
   */
  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const filename = generateFilename(themedSpec);

      if (vegaViewRef.current) {
        // Primary path: use vega's built-in PNG export (scale ×2 for retina quality)
        const url = await vegaViewRef.current.toImageURL('png', 2);
        triggerDownload(url, filename);
      } else {
        // Fallback path: render the SVG element to a canvas manually
        const svgEl = chartRef.current?.querySelector('svg');
        if (!svgEl) {
          console.warn('[VEGA CHART] No SVG element found for download fallback');
          return;
        }
        const pngUrl = await svgToPng(svgEl);
        triggerDownload(pngUrl, filename);
      }
    } catch (err) {
      console.error('[VEGA CHART] Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [themedSpec]);

  if (!themedSpec) {
    return <div className="vega-chart-container">Loading chart...</div>;
  }

  return (
    <div className="vega-chart-wrapper">
      <div ref={chartRef} className="vega-chart-container" style={{ border: '2px solid red' }} />
      <button
        type="button"
        className="chart-download-btn"
        onClick={handleDownload}
        disabled={isDownloading}
        aria-label="Download chart as PNG"
        title="Download chart as PNG"
      >
        {isDownloading ? (
          <svg
            className="chart-download-spinner"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
        <span>{isDownloading ? 'Downloading…' : 'Download PNG'}</span>
      </button>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function triggerDownload(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Convert an SVG element to a PNG data-URL via an off-screen canvas.
 * Adds a dark background matching the app theme.
 */
function svgToPng(svgEl) {
  return new Promise((resolve, reject) => {
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const scale = 2; // retina quality
      const canvas = document.createElement('canvas');
      canvas.width = (img.naturalWidth || svgEl.clientWidth || 800) * scale;
      canvas.height = (img.naturalHeight || svgEl.clientHeight || 400) * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

export default VegaChart;
